/**
 * Copy Shapes Tool
 * 
 * Copy shapes from template components or slides to the current slide.
 * Supports position overrides, text replacement, and group scaling.
 * 
 * IMPORTANT: Office.js does NOT support shape copying/cloning for PowerPoint.
 * This tool uses COM exclusively via the Python backend.
 * 
 * Note: Does NOT support metadata - use liveBuildArea.copy for that.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    CopyShapesParamsSchema,
    type CopyShapesParams,
    type ShapeToCopy,
} from "./copyShapes.schema";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Copied shape result
 */
export interface CopiedShapeResult {
    sourceShapeId: number;
    newShapeId: number;
    position: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
}

/**
 * Copy shapes result
 */
export interface CopyShapesResult {
    success: boolean;
    slideId: number;
    copiedShapes: CopiedShapeResult[];
    warnings: string[];
    errors: string[];
    executionMethod: "com";
}

// ═══════════════════════════════════════════════════════════════════════════
// COPY SHAPES TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
const COPY_SHAPES_SYSTEM_PROMPT = `### copyShapes
Use this tool to copy shapes onto the current slide, duplicate a pattern across a slide, or extend an existing grid.

**When to use:**
- Copying shapes from templates to maintain consistent styling
- Duplicating repeated elements across a slide
- Extending an existing grid by inserting a new row/column

**Features:**
- Copy + override: copy shapes and optionally adjust position or text
- Scale to target area: resize copied groups to fit a smaller or larger space
- Grid insertion: add a new row/column into an existing grid and keep the pattern consistent

**Text replacement requirement (IMPORTANT):**
When using \`textContent.paragraphs[]\` with \`setText\` or \`replaceText\`, you MUST include \`textFormat\` runs
to preserve styling. The schema enforces this to avoid losing run-level formatting.

**Grid insertion guidance:** Define the grid cells (each cell lists its shapes), then choose which row/column to duplicate and where to insert it. Use \`sizeMode: "fit"\` to keep the grid within the original bounds, or \`sizeMode: "grow"\` to expand the grid when there is enough empty space.

**Target area scaling (use cases):** Fit a copied block into a smaller placeholder area, expand a copied block to fill a larger section, or normalize multiple copied blocks to the same size region.

**Note:** This tool does not attach metadata. Use liveBuildArea with type="copy" if you need metadata entries.

\`\`\`json
{
  "slideId": 1,
  "sourceSlideId": 5,
  "shapes": [
    { 
      "shapeId": 201, 
      "targetPosition": { "left": 50, "top": 100 },
      "textContent": { "paragraphs": [{ "setText": "New title", "textFormat": [{ "text": "New title" }] }] }
    }
  ]
}
\`\`\``;

/**
 * Tool for copying shapes from templates or other slides.
 * 
 * Features:
 * - Copy shapes from template components or other slides
 * - Position overrides for individual shapes
 * - Text replacement using textContent
 * - Group scaling to fit target area
 * 
 * IMPORTANT: This tool uses COM exclusively because Office.js does not
 * support shape copying/cloning operations in PowerPoint.
 * 
 * Note: Does NOT support metadata/citations. Use liveBuildArea with type="copy"
 * if you need to attach metadata entries to copied shapes.
 */
export class CopyShapesTool extends ToolBase<CopyShapesParams, CopyShapesResult> {
    public readonly name = "copyShapes";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = COPY_SHAPES_SYSTEM_PROMPT;
    
    // Streaming operation fields
    fieldsToCheck = [
        { name: 'slideId', type: 'number' as const, list_status: false, order: 1 },
    ];
    
    listsToCheck = [];

    constructor() {
        super();
        this.description = new ToolDescription(
            "copyShapes",
            `Copy shapes from template components or other slides to the current slide.

**Purpose:** Copy and reposition shapes from templates.

**Features:**
- Copy from template components by componentId
- Copy from slides within the current presentation
- Position overrides per shape
- Text replacement using textContent
- Group scaling to fit a target area
- Grid row/column insertion for existing grids

**Text replacement requirement (IMPORTANT):**
When using \`textContent.paragraphs[]\` with \`setText\` or \`replaceText\`, you MUST include \`textFormat\` runs
to preserve styling (schema validation enforces this).

**Note:** Does NOT support metadata/citations. Use \`liveBuildArea\` with \`type: "copy"\` if you need to attach metadata entries.

**Example (Copy from same presentation):**
\`\`\`json
{
  "slideId": 1,
  "copySource": {
    "slideId": 3,
    "shapes": [
      {
        "shapeId": 5,
        "targetPosition": { "left": 100, "top": 200, "width": 150, "height": 80 }
      }
    ]
  }
}
\`\`\`

**Example (Copy from template component):**
\`\`\`json
{
  "slideId": 1,
  "copySource": {
    "componentId": "kpi_template",
    "slideId": 1,
    "shapes": [
      {
        "shapeId": 5,
        "targetPosition": { "left": 100, "top": 200, "width": 150, "height": 80 },
        "textContent": {
          "paragraphs": [
            { "replaceText": { "find": "{{title}}", "replace": "Revenue" }, "textFormat": [{ "text": "Revenue" }] }
          ]
        }
      }
    ],
    "targetArea": { "left": 50, "top": 100, "width": 400, "height": 200 }
  }
}
\`\`\`

**Group Scaling:**
When \`targetArea\` is specified, all copied shapes are scaled proportionally 
to fit within the target area while maintaining relative positions.

**Grid Insert:**
Insert rows or columns into an existing grid layout. Provide \`gridLayout\` and
\`gridInsert\` to copy a template row/column and optionally scale everything
back into the original bounds.`,
            CopyShapesParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CopyShapesResult> {
        const parseResult = safeParseToolParams(CopyShapesParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data;
        const { copySource } = params;
        
        const shapeCount = copySource.shapes?.length ?? 0;
        const isGridInsert = !!copySource.gridInsert;
        console.log(
            `[CopyShapesTool] Copying ${shapeCount} shapes to slide ${params.slideId}`
            + (isGridInsert ? " (grid insert mode)" : "")
        );
        if (copySource.componentId) {
            console.log(`  Source: component "${copySource.componentId}", slide ${copySource.slideId ?? 1}`);
        } else {
            console.log(`  Source: slide ${copySource.slideId}`);
        }

        // Office.js does NOT support shape copying - use COM directly
        try {
            const comResult = await this.executeWithCOM(params);
            return comResult;
        } catch (comError) {
            const comErrorMsg = comError instanceof Error ? comError.message : String(comError);
            console.error(`[CopyShapesTool] COM failed:`, comErrorMsg);
            
            return {
                success: false,
                slideId: params.slideId,
                copiedShapes: [],
                warnings: [],
                errors: [`COM error: ${comErrorMsg}`],
                executionMethod: "com" as const,
            };
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // COM IMPLEMENTATION (REQUIRED - Office.js doesn't support copy)
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Execute shape copying using Python COM backend
     * 
     * This is the only implementation because Office.js does not support
     * shape copying/cloning operations in PowerPoint.
     */
    private async executeWithCOM(params: CopyShapesParams): Promise<CopyShapesResult> {
        const warnings: string[] = [];
        const errors: string[] = [];

        // Get deck ID for COM connection
        const documentId = await getOrCreatedocumentId();
        console.log(`[CopyShapesTool] Using documentId: ${documentId}`);

        // Build request payload for Python COM backend
        const comPayload = {
            documentId,
            targetSlideId: params.slideId,
            sourceComponentId: params.copySource.componentId,
            sourceSlideId: params.copySource.slideId,
            shapes: params.copySource.shapes?.map((shape: ShapeToCopy) => ({
                shapeId: shape.shapeId,
                targetPosition: shape.targetPosition ? {
                    left: shape.targetPosition.left,
                    top: shape.targetPosition.top,
                    width: shape.targetPosition.width,
                    height: shape.targetPosition.height,
                } : undefined,
                textReplacements: this.extractTextReplacements(shape),
            })) ?? [],
            targetArea: params.copySource.targetArea ? {
                left: params.copySource.targetArea.left,
                top: params.copySource.targetArea.top,
                width: params.copySource.targetArea.width,
                height: params.copySource.targetArea.height,
                scaleMode: params.copySource.targetArea.scaleMode,
            } : undefined,
            gridLayout: params.copySource.gridLayout ? {
                rows: params.copySource.gridLayout.rows,
                columns: params.copySource.gridLayout.columns,
                cells: params.copySource.gridLayout.cells.map(cell => ({
                    row: cell.row,
                    column: cell.column,
                    shapeIds: cell.shapeIds,
                })),
                gridBounds: params.copySource.gridLayout.gridBounds ? {
                    left: params.copySource.gridLayout.gridBounds.left,
                    top: params.copySource.gridLayout.gridBounds.top,
                    width: params.copySource.gridLayout.gridBounds.width,
                    height: params.copySource.gridLayout.gridBounds.height,
                } : undefined,
            } : undefined,
            gridInsert: params.copySource.gridInsert ? {
                axis: params.copySource.gridInsert.axis,
                insertIndex: params.copySource.gridInsert.insertIndex,
                position: params.copySource.gridInsert.position,
                count: params.copySource.gridInsert.count,
                templateIndex: params.copySource.gridInsert.templateIndex,
                sizeMode: params.copySource.gridInsert.sizeMode,
                preserveAspectRatio: params.copySource.gridInsert.preserveAspectRatio,
                scaleMode: params.copySource.gridInsert.scaleMode,
            } : undefined,
        };

        console.log(`[CopyShapesTool] Calling COM backend:`, comPayload);

        // Call Python COM backend
        const response = await fetch(`${BACKEND_BASE_URL}/com/copyShapes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(comPayload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`COM backend error (${response.status}): ${errorText}`);
        }

        const comResult = await response.json();
        console.log(`[CopyShapesTool] COM backend response:`, comResult);

        if (!comResult.success) {
            errors.push(comResult.message || 'COM operation failed');
        }

        const copiedShapes: CopiedShapeResult[] = (comResult.copiedShapes || []).map((cs: any) => ({
            sourceShapeId: cs.sourceShapeId,
            newShapeId: cs.newShapeId,
            position: {
                left: cs.position?.left ?? 0,
                top: cs.position?.top ?? 0,
                width: cs.position?.width ?? 100,
                height: cs.position?.height ?? 50,
            },
        }));

        console.log(`[CopyShapesTool] Copy complete:`,
            `${copiedShapes.length} shapes copied, ${warnings.length} warnings, ${errors.length} errors`);

        return {
            success: comResult.success,
            slideId: params.slideId,
            copiedShapes,
            warnings: [...warnings, ...(comResult.warnings || [])],
            errors: [...errors, ...(comResult.errors || [])],
            executionMethod: "com" as const,
        };
    }

    /**
     * Extract text replacement operations from shape copy params
     */
    private extractTextReplacements(shape: ShapeToCopy): Array<{ find: string; replace: string }> {
        const replacements: Array<{ find: string; replace: string }> = [];
        
        if (shape.textContent?.paragraphs) {
            for (const para of shape.textContent.paragraphs) {
                if (para.replaceText) {
                    replacements.push({
                        find: para.replaceText.find,
                        replace: para.replaceText.replace,
                    });
                }
            }
        }
        
        return replacements;
    }
}
